import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plug, Database } from 'lucide-react';

const SOURCES = [
  { key: 'cloudtrail', label: 'CloudTrail', status: 'live', ingestedText: '4 min ago', count: 127847, retention: '14 months · Object Lock' },
  { key: 'config', label: 'Config', status: 'live', ingestedText: '4 min ago', count: 1247, retention: '14 months · Object Lock' },
  { key: 'inspector', label: 'Inspector', status: 'live', ingestedText: '12 min ago', count: 127000, retention: '14 months' },
  { key: 'macie', label: 'Macie', status: 'live', ingestedText: '1 hr ago', count: 23, retention: '14 months' },
  { key: 'github', label: 'GitHub', status: 'live', ingestedText: '8 min ago', count: 847, retention: '90 days' },
  { key: 'purview', label: 'Purview', status: 'not_connected', ingestedText: 'Not connected', count: 0, retention: '—' },
  { key: 'servicenow', label: 'ServiceNow', status: 'not_connected', ingestedText: 'Not connected', count: 0, retention: '—' },
];

export default function EvidenceFabricHealth() {
  return (
    <div data-testid="evidence-fabric-health" className="bg-apex-shell text-slate-300 rounded-lg px-4 py-3">
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
        <div className="flex items-center gap-2 shrink-0">
          <Database className="w-3.5 h-3.5 text-apex-lens" />
          <span className="text-[10px] uppercase tracking-widest font-mono text-slate-400">Evidence Fabric</span>
        </div>

        <div className="flex-1 min-w-0 overflow-x-auto">
          <div className="flex items-center gap-2 md:gap-3 whitespace-nowrap py-0.5">
            {SOURCES.map((s) => (
              <Pill key={s.key} source={s} />
            ))}
          </div>
        </div>

        <div className="shrink-0 text-[11px] font-mono text-apex-lens">
          <span className="hidden sm:inline">98.7% coverage</span>
          <span className="sm:hidden">98.7%</span>
        </div>
      </div>

      {/* Partial coverage notice — honesty signal for enterprise evaluators */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-2 pt-2 border-t border-slate-700/50 flex flex-wrap items-center gap-x-4 gap-y-1"
        data-testid="fabric-coverage-warning"
      >
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-amber-400">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
          CloudTrail data events not enabled in stratum-dev-sandbox · S3 object-level activity unavailable for dev account
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-500 shrink-0" />
          VPC Flow Logs enabled in 39 / 47 accounts · Network traversal evidence partial
        </div>
      </motion.div>
    </div>
  );
}

function Pill({ source }) {
  const live = source.status === 'live';
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`fabric-pill-${source.key}`}
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-mono transition-colors ${live ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15' : 'border-slate-700 bg-slate-800/60 text-slate-400 hover:bg-slate-800'}`}
        >
          <span className="relative inline-block w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: live ? '#10B981' : '#475569' }}>
            {live && (
              <motion.span
                className="absolute inset-0 rounded-full"
                style={{ backgroundColor: '#10B981' }}
                animate={{ opacity: [0.6, 0, 0.6], scale: [1, 1.8, 1] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
              />
            )}
          </span>
          <span>{source.label}</span>
          <span className="opacity-70">{source.ingestedText}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" className="w-72 bg-apex-shell text-slate-200 border-slate-700">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">{source.label}</h4>
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${live ? 'text-emerald-400' : 'text-slate-400'}`}>
              {live ? 'Live' : 'Not connected'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1 text-[11px] font-mono">
            <span className="text-slate-500">Last ingest</span><span className="text-right">{source.ingestedText}</span>
            <span className="text-slate-500">Records 24h</span><span className="text-right">{live ? source.count.toLocaleString() : '—'}</span>
            <span className="text-slate-500">Retention</span><span className="text-right">{source.retention}</span>
          </div>
          {!live && (
            <Link to="/lens/integrations" className="block mt-2">
              <Button size="sm" className="w-full bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5">
                <Plug className="w-3.5 h-3.5" />
                Connect {source.label}
              </Button>
            </Link>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
